import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, mediaUrl } from "@/lib/api";
import { Field, FileField, inputCls } from "./fields";

const EMPTY = { title: "", cover_url: "", body: "", date: "", featured: false };

export default function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/news").then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/news/${editing.id}`, editing);
      else await api.post("/admin/news", editing);
      toast.success("Post saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/news/${id}`);
    load();
  };

  return (
    <div data-testid="admin-news">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl text-slate-50">News Posts</h1>
        <button onClick={() => setEditing({ ...EMPTY, date: new Date().toISOString().slice(0, 10) })}
          className="btn-gold !px-5 !py-2 text-xs" data-testid="news-new-btn">
          <Plus size={14} /> New Post
        </button>
      </div>
      {editing && (
        <div className="rounded-2xl border border-gold-400/25 bg-navy-900/70 p-6 mb-8 space-y-4" data-testid="news-form">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-lg text-slate-100">{editing.id ? "Edit Post" : "New Post"}</h2>
            <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-200"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Title"><input className={inputCls} value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="news-title-input" /></Field>
            <Field label="Date"><input type="date" className={inputCls} value={editing.date}
              onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid="news-date-input" /></Field>
          </div>
          <Field label="Body"><textarea rows={8} className={inputCls} value={editing.body}
            onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="news-body-input" /></Field>
          <FileField label="Cover image" accept="image/*" value={editing.cover_url}
            onChange={(url) => setEditing({ ...editing, cover_url: url })} testid="news-cover-upload" />
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" className="accent-[#D4AF37]" checked={editing.featured}
              onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="news-featured-check" />
            Featured
          </label>
          <button onClick={save} className="btn-gold !px-6 !py-2.5 text-xs" data-testid="news-save-btn">Save Post</button>
        </div>
      )}
      <div className="space-y-2">
        {items.map((n) => (
          <div key={n.id} className="flex items-center gap-4 rounded-xl border border-white/5 bg-navy-900/50 px-4 py-3" data-testid={`news-row-${n.id}`}>
            {n.cover_url && <img src={mediaUrl(n.cover_url)} alt="" className="w-10 h-10 rounded-md object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-100 truncate">{n.title}</p>
              <p className="text-[11px] text-slate-500">{n.date}{n.featured && <span className="text-gold-400 ml-2">Featured</span>}</p>
            </div>
            <button onClick={() => setEditing({ ...EMPTY, ...n })} className="text-slate-400 hover:text-gold-300 p-2" data-testid={`news-edit-${n.id}`}><Pencil size={15} /></button>
            <button onClick={() => del(n.id)} className="text-slate-500 hover:text-red-400 p-2" data-testid={`news-delete-${n.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-500">No posts yet.</p>}
      </div>
    </div>
  );
}
